import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  ChevronDown,
  ChevronLeft,
  Close,
  Document,
} from "@carbon/icons-react";
import Constants from "../../utils/Constants";
import SplashScreen from "../../components/loader/SplashScreen/SplashScreen";
import {
  STATUS_OPTIONS,
  formatLabel,
  statusColor,
} from "./sheRunsLabels";

const SECTION_IDS = [
  "personal",
  "fitness",
  "story",
  "commitment",
  "documents",
  "review",
];

const QAItem = ({ question, answer }) => (
  <div
    style={{
      padding: "14px 0",
      borderBottom: "1px solid #f0f0f0",
    }}
  >
    <p
      style={{
        margin: "0 0 6px",
        fontSize: 13,
        lineHeight: 1.5,
        color: "#777",
        fontWeight: 500,
      }}
    >
      {question}
    </p>
    <p
      style={{
        margin: 0,
        fontSize: 15,
        lineHeight: 1.6,
        color: "#222",
        fontWeight: 600,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {answer || "-"}
    </p>
  </div>
);

const AccordionSection = ({ id, title, open, onToggle, children, count }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e5e5e5",
      borderRadius: 8,
      marginBottom: 12,
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      overflow: "hidden",
    }}
  >
    <button
      type="button"
      onClick={() => onToggle(id)}
      aria-expanded={open}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "16px 20px",
        background: open ? "#fafafa" : "#fff",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: "#222",
          }}
        >
          {title}
        </span>
        {typeof count === "number" && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#888",
              background: "#f0f0f0",
              borderRadius: 10,
              padding: "2px 8px",
            }}
          >
            {count}
          </span>
        )}
      </span>
      <ChevronDown
        size={20}
        style={{
          color: "#666",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s ease",
          flexShrink: 0,
        }}
      />
    </button>
    {open && (
      <div style={{ padding: "4px 20px 16px", borderTop: "1px solid #eee" }}>
        {children}
      </div>
    )}
  </div>
);

const buildFileUrl = (path) => {
  if (!path) return null;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${Constants.url}image${normalized}`;
};

const ImageLightbox = ({ open, src, label, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open || !src) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label || "Image preview"}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10050,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          maxWidth: "min(960px, 96vw)",
          maxHeight: "90vh",
          background: "#111",
          borderRadius: 10,
          padding: 12,
          boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 10,
            padding: "0 4px",
          }}
        >
          <span style={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
            {label}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              border: "none",
              borderRadius: 6,
              background: "rgba(255,255,255,0.12)",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            <Close size={20} />
          </button>
        </div>
        <img
          src={src}
          alt={label}
          style={{
            display: "block",
            maxWidth: "100%",
            maxHeight: "calc(90vh - 64px)",
            objectFit: "contain",
            borderRadius: 6,
            margin: "0 auto",
          }}
        />
      </div>
    </div>
  );
};

const AuthFilePreview = ({ path, label, compact = false }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const isPdf = path ? /\.pdf$/i.test(path) : false;
  const remoteUrl = buildFileUrl(path);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;

    if (!remoteUrl || isPdf) {
      setPreviewUrl(null);
      setError(false);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(false);

    axios({
      method: "get",
      url: remoteUrl,
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setPreviewUrl(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [remoteUrl, isPdf]);

  const openPdf = async () => {
    if (!remoteUrl) return;

    try {
      const res = await axios({
        method: "get",
        url: remoteUrl,
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const objectUrl = URL.createObjectURL(res.data);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch {
      window.open(remoteUrl, "_blank", "noopener,noreferrer");
    }
  };

  const openImagePopup = () => {
    if (previewUrl) setLightboxOpen(true);
  };

  return (
    <div
      style={{
        padding: compact ? "8px 0 4px" : "14px 0",
        borderBottom: compact ? "none" : "1px solid #f0f0f0",
        height: "100%",
      }}
    >
      <p
        style={{
          margin: "0 0 8px",
          fontSize: 13,
          lineHeight: 1.5,
          color: "#777",
          fontWeight: 500,
        }}
      >
        {label}
      </p>
      {!path ? (
        <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "#222" }}>
          -
        </p>
      ) : isPdf ? (
        <button
          type="button"
          className="uk-button uk-button-default uk-button-small"
          onClick={openPdf}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <Document size={16} />
          Open PDF
        </button>
      ) : (
        <div
          style={{
            minHeight: compact ? 140 : 80,
            display: "flex",
            alignItems: "center",
            justifyContent: compact ? "center" : "flex-start",
            background: "#fafafa",
            border: "1px solid #e5e5e5",
            borderRadius: 8,
            padding: 8,
            cursor: previewUrl ? "pointer" : "default",
          }}
          onClick={previewUrl ? openImagePopup : undefined}
          role={previewUrl ? "button" : undefined}
          tabIndex={previewUrl ? 0 : undefined}
          onKeyDown={(e) => {
            if (previewUrl && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              openImagePopup();
            }
          }}
        >
          {loading && (
            <span className="uk-text-muted" style={{ fontSize: 13 }}>
              Loading preview…
            </span>
          )}
          {!loading && error && (
            <span className="uk-text-danger" style={{ fontSize: 13 }}>
              Preview unavailable
            </span>
          )}
          {!loading && previewUrl && (
            <img
              src={previewUrl}
              alt={label}
              style={{
                width: "100%",
                maxHeight: compact ? 180 : 200,
                borderRadius: 6,
                objectFit: "contain",
                pointerEvents: "none",
              }}
            />
          )}
        </div>
      )}

      <ImageLightbox
        open={lightboxOpen}
        src={previewUrl}
        label={label}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const SheRunsApplicationDetail = ({ applicationId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("pending");
  const [reviewNotes, setReviewNotes] = useState("");
  const [openSections, setOpenSections] = useState(() => new Set(SECTION_IDS));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    axios({
      method: "get",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      url: Constants.url + `she-runs-applications/${applicationId}`,
    })
      .then((res) => {
        if (cancelled) return;
        const app = res.data?.data?.data || res.data?.data;
        setData(app);
        setStatus(app?.status || "pending");
        setReviewNotes(app?.reviewNotes || "");
      })
      .catch((err) => {
        if (cancelled) return;
        Swal.fire(
          "Error",
          err?.response?.data?.message || "Failed to load application",
          "error"
        );
        onBack(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const toggleSection = (id) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenSections(new Set(SECTION_IDS));
  const collapseAll = () => setOpenSections(new Set());

  const handleUpdateStatus = () => {
    if (!status) {
      Swal.fire("Error", "Please select a status", "error");
      return;
    }

    setSaving(true);
    axios({
      method: "patch",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
      url: Constants.url + `she-runs-applications/${applicationId}/status`,
      data: {
        status,
        ...(reviewNotes?.trim() ? { reviewNotes: reviewNotes.trim() } : {}),
      },
    })
      .then((res) => {
        const updated = res.data?.data?.data || res.data?.data;
        if (updated) {
          setData((prev) => ({ ...prev, ...updated }));
          setStatus(updated.status || status);
          setReviewNotes(updated.reviewNotes || reviewNotes);
        }
        Swal.fire(
          "Success",
          res.data?.message || "Application status updated successfully",
          "success"
        );
      })
      .catch((err) => {
        Swal.fire(
          "Error",
          err?.response?.data?.message || "Failed to update status",
          "error"
        );
      })
      .finally(() => setSaving(false));
  };

  if (loading || !data) {
    return <SplashScreen />;
  }

  const isOpen = (id) => openSections.has(id);

  return (
    <div className="dashboardWrp">
      <div className="uk-container uk-container-expand">
        <div className="uk-margin-bottom uk-flex uk-flex-between uk-flex-middle uk-flex-wrap" style={{ gap: 12 }}>
          <button
            className="uk-button uk-button-default uk-button-small"
            onClick={() => onBack(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 16px",
              borderRadius: "4px",
              backgroundColor: "#f8f8f8",
              border: "1px solid #e5e5e5",
              color: "#666",
              fontWeight: "500",
              fontSize: "14px",
            }}
          >
            <ChevronLeft size={18} />
            Back to list
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="uk-button uk-button-text uk-button-small"
              onClick={expandAll}
            >
              Expand all
            </button>
            <button
              type="button"
              className="uk-button uk-button-text uk-button-small"
              onClick={collapseAll}
            >
              Collapse all
            </button>
          </div>
        </div>

        {/* Pinned status / review card */}
        <div
          className="uk-card uk-card-default uk-margin-bottom"
          style={{
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            borderRadius: "8px",
            border: "1px solid #e5e5e5",
            position: "sticky",
            top: 12,
            zIndex: 5,
          }}
        >
          <div
            className="uk-card-header"
            style={{
              backgroundColor: "#fafafa",
              borderBottom: "1px solid #e5e5e5",
              padding: "18px 24px",
            }}
          >
            <div
              className="uk-flex uk-flex-between uk-flex-middle uk-flex-wrap"
              style={{ gap: 12 }}
            >
              <div>
                <h3
                  className="uk-card-title uk-margin-remove"
                  style={{ fontSize: 22, fontWeight: 600, color: "#333" }}
                >
                  {data.fullName || "SHE RUNS Application"}
                </h3>
                <p className="uk-text-muted uk-margin-remove-top uk-margin-small-bottom">
                  {data.cnic || "—"} · {data.email || "—"} · {data.phone || "—"}
                </p>
              </div>
              <span
                style={{
                  display: "inline-block",
                  padding: "4px 14px",
                  borderRadius: "16px",
                  fontSize: "12px",
                  fontWeight: "600",
                  backgroundColor: statusColor(data.status),
                  color: "#fff",
                }}
              >
                {formatLabel(data.status)}
              </span>
            </div>
          </div>

          <div className="uk-card-body" style={{ padding: "20px 24px" }}>
            <div
              className="uk-grid uk-grid-small uk-child-width-1-3@m"
              uk-grid=""
            >
              <div>
                <label className="uk-form-label">Update status</label>
                <select
                  className="uk-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.filter((o) => o.value).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="uk-width-1-1 uk-width-2-3@m">
                <label className="uk-form-label">Review notes (optional)</label>
                <input
                  className="uk-input"
                  type="text"
                  maxLength={500}
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Optional notes, max 500 characters"
                />
              </div>
            </div>
            <button
              className="uk-button uk-button-primary uk-margin-small-top"
              onClick={handleUpdateStatus}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save status"}
            </button>
          </div>
        </div>

        <AccordionSection
          id="personal"
          title="Personal details"
          open={isOpen("personal")}
          onToggle={toggleSection}
          count={7}
        >
          <div
            className="uk-grid uk-grid-small uk-child-width-1-2@m"
            uk-grid=""
          >
            <div>
              <QAItem question="Full name" answer={data.fullName} />
            </div>
            <div>
              <QAItem
                question="Date of birth"
                answer={formatDateOnly(data.dob)}
              />
            </div>
            <div>
              <QAItem question="CNIC" answer={data.cnic} />
            </div>
            <div>
              <QAItem question="Phone" answer={data.phone} />
            </div>
            <div>
              <QAItem question="Email" answer={data.email} />
            </div>
            <div>
              <QAItem question="Area" answer={data.area} />
            </div>
            <div className="uk-width-1-1">
              <QAItem question="Occupation" answer={data.occupation} />
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
              marginTop: 8,
              paddingTop: 8,
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <AuthFilePreview
              label="CNIC front"
              path={data.cnicFront}
              compact
            />
            <AuthFilePreview
              label="CNIC back"
              path={data.cnicBack}
              compact
            />
          </div>
        </AccordionSection>

        <AccordionSection
          id="fitness"
          title="Running and fitness background"
          open={isOpen("fitness")}
          onToggle={toggleSection}
          count={5}
        >
          <QAItem
            question="What is your running or sports experience?"
            answer={formatLabel(data.sportsExperience)}
          />
          {data.sportsExperienceOther ? (
            <QAItem
              question="Sports experience (other)"
              answer={data.sportsExperienceOther}
            />
          ) : null}
          <QAItem
            question="How often do you currently exercise?"
            answer={formatLabel(data.exerciseFrequency)}
          />
          <QAItem
            question="What is the longest distance you have run or jogged without stopping?"
            answer={formatLabel(data.longestDistance)}
          />
          <QAItem
            question="Have you ever taken part in a race or sports competition? If yes, tell us about it."
            answer={data.raceExperience}
          />
          <QAItem
            question="Do you have any injuries or medical conditions we should know about?"
            answer={data.injuries}
          />
        </AccordionSection>

        <AccordionSection
          id="story"
          title="Your story"
          open={isOpen("story")}
          onToggle={toggleSection}
          count={3}
        >
          <QAItem
            question="Why do you want to run a marathon?"
            answer={data.whyMarathon}
          />
          <QAItem
            question="What has stopped you from running or pursuing sports so far? (Select only 3)"
            answer={formatLabel(data.barriers)}
          />
          {data.barriersOther ? (
            <QAItem question="Other barrier" answer={data.barriersOther} />
          ) : null}
          <QAItem
            question="What would completing the BYD Karachi Marathon 2027 mean to you?"
            answer={data.marathonMeaning}
          />
        </AccordionSection>

        <AccordionSection
          id="commitment"
          title="Commitment & availability"
          open={isOpen("commitment")}
          onToggle={toggleSection}
          count={4}
        >
          <QAItem
            question="The program runs from late October to 3 January 2027 with regular training sessions. Can you commit to the full program?"
            answer={formatLabel(data.commitFullProgram)}
          />
          <QAItem
            question="Which times are you usually available to train?"
            answer={formatLabel(data.trainTimes)}
          />
          <QAItem
            question="Would you need transport support to attend training?"
            answer={formatLabel(data.transportSupport)}
          />
          <QAItem
            question="Are you aspiring to complete a half marathon (21Km) or full marathon (42Km)?"
            answer={formatLabel(data.marathonDistance)}
          />
        </AccordionSection>

        <AccordionSection
          id="documents"
          title="Documents & consent"
          open={isOpen("documents")}
          onToggle={toggleSection}
          count={4}
        >
          <AuthFilePreview
            label="Medical certificate"
            path={data.medicalCertificate}
          />
          <QAItem
            question="Do you agree to be photographed and filmed as part of the SHE RUNS campaign?"
            answer={formatLabel(data.photoConsent)}
          />
          <QAItem
            question="How did you hear about SHE RUNS?"
            answer={formatLabel(data.hearAbout)}
          />
          {data.hearAboutOther ? (
            <QAItem
              question="How did you hear about SHE RUNS? (other)"
              answer={data.hearAboutOther}
            />
          ) : null}
          <QAItem
            question="Declaration"
            answer={formatLabel(data.declaration)}
          />
        </AccordionSection>

        <AccordionSection
          id="review"
          title="Review metadata"
          open={isOpen("review")}
          onToggle={toggleSection}
          count={5}
        >
          <div
            className="uk-grid uk-grid-small uk-child-width-1-2@m"
            uk-grid=""
          >
            <div>
              <QAItem
                question="Submitted at"
                answer={formatDateTime(data.createdAt)}
              />
            </div>
            <div>
              <QAItem
                question="Updated at"
                answer={formatDateTime(data.updatedAt)}
              />
            </div>
            <div>
              <QAItem
                question="Reviewed at"
                answer={formatDateTime(data.reviewedAt)}
              />
            </div>
            <div>
              <QAItem
                question="Reviewed by"
                answer={
                  data.reviewedBy?.name ||
                  data.reviewedBy?.email ||
                  data.reviewedBy ||
                  "-"
                }
              />
            </div>
            <div className="uk-width-1-1">
              <QAItem question="Review notes" answer={data.reviewNotes} />
            </div>
          </div>
        </AccordionSection>
      </div>
    </div>
  );
};

export default SheRunsApplicationDetail;
